import { createContext, useContext, useEffect, useState } from 'react';
import { useAuthStore } from '@/lib/api-functions/api-auth';
import { useStringsData } from '@/lib/useStringsData';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';

// Create context with the same interface as useStringsData
const StringsDataContext = createContext<ReturnType<typeof useStringsData> | null>(null);

// Separate component that only renders when authenticated and has a selected game
const AuthenticatedStringsDataProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuthStore();
  const { selectedGame } = useSimplifiedUserData();
  const [isInitialized, setIsInitialized] = useState(false);
  
  const stringsData = useStringsData({
    gameId: selectedGame?.id,
    autoLoad: false,
  });

  // Load data only when not yet initialized and we have a selected game
  useEffect(() => {
    if (!isInitialized && selectedGame?.id) {
      stringsData.loadData();
      setIsInitialized(true);
    }
  }, [isInitialized, stringsData, selectedGame?.id]);

  // Reset initialization when selected game changes
  useEffect(() => {
    if (selectedGame?.id) {
      setIsInitialized(false);
    }
  }, [selectedGame?.id]);

  return (
    <StringsDataContext.Provider value={stringsData}>
      {children}
    </StringsDataContext.Provider>
  );
};

export const StringsDataProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <StringsDataContext.Provider value={null}>
        {children}
      </StringsDataContext.Provider>
    );
  }

  // If authenticated, render the authenticated provider
  if (isAuthenticated && user?.id) {
    return <AuthenticatedStringsDataProvider>{children}</AuthenticatedStringsDataProvider>;
  }

  // If not authenticated, provide null context
  return (
    <StringsDataContext.Provider value={null}>
      {children}
    </StringsDataContext.Provider>
  );
};

export const useStringsDataContext = () => {
  const context = useContext(StringsDataContext);
  if (!context) {
    throw new Error('useStringsDataContext must be used within StringsDataProvider and user must be authenticated');
  }
  return context;
}; 